import { Component, OnInit, ChangeDetectionStrategy, inject, signal, computed, ElementRef, HostListener, DestroyRef, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Razor UI Components
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { BadgeComponent } from '@shared/components/ui/badge/badge.component';
import { ChipComponent } from '@shared/components/ui/chip/chip.component';
import { TabsComponent, TabComponent } from '@shared/components/ui/tabs/tabs.component';
import { ToggleComponent } from '@shared/components/ui/toggle/toggle.component';
import { SliderComponent } from '@shared/components/ui/slider/slider.component';
import { DividerComponent } from '@shared/components/ui/divider/divider.component';
import { ModalComponent } from '@shared/components/ui/modal/modal.component';
import { TooltipDirective } from '@shared/components/ui/tooltip/tooltip.directive';
import { IconComponent } from '@shared/components/ui/icon/icon.component';

import {
  NotificationService,
  TradingAlert,
  NotificationAction,
  NotificationCategory,
  NotificationSeverity
} from '@core/services/notification.service';

@Component({
  selector: 'app-notification-center',
  host: { '(document:keydown.escape)': 'onEscape($event)' },
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    BadgeComponent,
    ChipComponent,
    TabsComponent,
    TabComponent,
    ToggleComponent,
    SliderComponent,
    DividerComponent,
    ModalComponent,
    TooltipDirective,
    IconComponent
  ],
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationCenterComponent implements OnInit {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Handle document clicks to close panel when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.panelOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  // Panel visibility state
  panelOpen = signal(false);
  confirmDialogOpen = signal(false);

  // State
  showSettings = signal(false);
  activeTab = signal(0);
  currentPage = signal(0);
  pageSize = 10;

  // Settings
  soundEnabled = signal(true);
  desktopEnabled = signal(true);
  toastEnabled = signal(true);
  soundVolume = signal(50);

  readonly settingsStatus = this.notificationService.settingsStatus;
  readonly settingsReady = this.notificationService.settingsReady;

  retrySettings(event?: Event): void {
    event?.stopPropagation();
    this.notificationService.retrySettings();
  }

  // Computed values from service
  alerts = this.notificationService.recentAlerts;
  unreadCount = this.notificationService.unreadCount;
  criticalAlerts = this.notificationService.criticalAlerts;
  stats = this.notificationService.stats;

  // Local computed values
  hasUnreadCritical = computed(() => this.criticalAlerts().length > 0);

  totalPages = computed(() =>
    Math.ceil(this.alerts().length / this.pageSize)
  );

  paginatedAlerts = computed(() => {
    const start = this.currentPage() * this.pageSize;
    const end = start + this.pageSize;
    return this.alerts().slice(start, end);
  });

  constructor() {
    effect(() => {
      const settings = this.notificationService.currentSettings();
      this.soundEnabled.set(settings.enableSound);
      this.desktopEnabled.set(settings.enableDesktop);
      this.toastEnabled.set(settings.enableToast);
      this.soundVolume.set(settings.soundVolume * 100);
    });
  }

  onEscape(event: Event): void {
    if (!this.panelOpen() || this.confirmDialogOpen() || event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : null;
    const dialog = target?.closest('dialog, [role="dialog"], [role="alertdialog"]');
    if (dialog && !this.elementRef.nativeElement.contains(dialog)) return;
    event.preventDefault();
    event.stopPropagation();
    this.closePanel();
    this.elementRef.nativeElement.querySelector('[data-testid="notification-bell"] button')?.focus();
  }

  ngOnInit(): void {
    // Subscribe to alert actions with automatic cleanup on component destroy
    this.notificationService.action$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ alert, action }) => {
        this.handleAction(alert, action);
      });
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    this.panelOpen.update(v => !v);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.showSettings.set(false);
  }

  toggleSettings(): void {
    this.showSettings.update(value => !value);
  }

  closeSettings(event?: Event): void {
    event?.stopPropagation();
    this.showSettings.set(false);
  }

  toggleSound(): void {
    this.updateSettings();
  }

  updateSettings(): void {
    this.notificationService.updateSettings({
      enableSound: this.soundEnabled(),
      enableDesktop: this.desktopEnabled(),
      enableToast: this.toastEnabled(),
      soundVolume: this.soundVolume() / 100
    });
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  confirmClearAll(): void {
    this.confirmDialogOpen.set(true);
  }

  executeClearAll(): void {
    this.notificationService.clearAllAlerts();
    this.confirmDialogOpen.set(false);
  }

  cancelClearAll(): void {
    this.confirmDialogOpen.set(false);
  }

  handleAlertClick(alert: TradingAlert): void {
    if (!alert.read) {
      this.notificationService.markAsRead(alert.id);
    }

    // Handle default action based on category. `metadata` is structurally
    // an opaque record on the model, so we index into it via bracket access.
    switch (alert.category) {
      case 'order': {
        const orderId = alert.metadata?.['orderId'];
        if (orderId) {
          this.router.navigate(['/trading/orders', orderId]);
        }
        break;
      }
      case 'position': {
        const positionId = alert.metadata?.['positionId'];
        if (positionId) {
          this.router.navigate(['/trading/positions', positionId]);
        }
        break;
      }
      case 'risk':
        // GAP-FE-H001 · /risk/dashboard does not exist; the canonical
        // route is /risk-management.
        this.router.navigate(['/risk-management']);
        break;
    }
  }

  removeAlert(alert: TradingAlert, event: Event): void {
    event.stopPropagation();
    this.notificationService.removeAlert(alert.id);
  }

  executeAction(alert: TradingAlert, action: NotificationAction, event: Event): void {
    event.stopPropagation();
    this.notificationService.executeAction(alert, action);
  }

  handleAction(alert: TradingAlert, action: NotificationAction): void {
    switch (action.action) {
      case 'view_order':
        this.router.navigate(['/trading/orders', action.data]);
        break;
      case 'close_position': {
        const positionId = (action.data as { positionId?: string } | undefined)?.positionId ?? '';
        this.router.navigate(['/trading/positions', positionId], {
          queryParams: { action: 'close' }
        });
        break;
      }
      case 'view_risk':
        // GAP-FE-H001 · /risk/report does not exist; route to the
        // canonical risk-management surface and let it surface
        // reporting from there.
        this.router.navigate(['/risk-management']);
        break;
      // Add more action handlers as needed
    }
  }

  getAlertIcon(alert: TradingAlert): string {
    switch (alert.severity) {
      case 'critical': return 'exclamation-circle';
      case 'error': return 'times-circle';
      case 'warning': return 'exclamation-triangle';
      case 'success': return 'check-circle';
      default: return 'info-circle';
    }
  }

  /**
   * Get CSS variable name for alert color
   * Returns CSS custom property aligned with design system
   */
  getAlertColorClass(alert: TradingAlert): string {
    switch (alert.severity) {
      case 'critical': return 'text-error';
      case 'error': return 'text-error';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      default: return 'text-info';
    }
  }

  getSeverityIcon(severity: NotificationSeverity): string {
    switch (severity) {
      case 'critical': return 'ban';
      case 'error': return 'times-circle';
      case 'warning': return 'exclamation-triangle';
      case 'success': return 'check-circle';
      default: return 'info-circle';
    }
  }

  /**
   * Get CSS variable name for severity color
   * Returns CSS custom property aligned with design system
   */
  getSeverityColorClass(severity: NotificationSeverity): string {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'error': return 'text-error';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      default: return 'text-info';
    }
  }

  getCategorySeverity(category: NotificationCategory): string {
    switch (category) {
      case 'risk': return 'danger';
      case 'compliance': return 'warning';
      case 'trade': return 'success';
      case 'order': return 'info';
      default: return 'secondary';
    }
  }

  getActionVariant(action: NotificationAction): 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' {
    switch (action.style) {
      case 'danger': return 'danger';
      case 'secondary': return 'secondary';
      default: return 'primary';
    }
  }

  getChipVariant(category: NotificationCategory): 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' {
    switch (category) {
      case 'risk': return 'danger';
      case 'compliance': return 'warning';
      case 'trade': return 'success';
      case 'order': return 'info';
      default: return 'default';
    }
  }

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {return `${days}d ago`;}
    if (hours > 0) {return `${hours}h ago`;}
    if (minutes > 0) {return `${minutes}m ago`;}
    return 'Just now';
  }

  getCategories(): NotificationCategory[] {
    return ['trade', 'order', 'position', 'risk', 'market', 'system', 'compliance', 'connection'];
  }

  getSeverities(): NotificationSeverity[] {
    return ['critical', 'error', 'warning', 'info', 'success'];
  }

  previousPage(): void {
    if (this.currentPage() > 0) {
      this.currentPage.update(p => p - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.update(p => p + 1);
    }
  }
}
